<template>
  <div class="flex flex-col flex-1 h-full m-0 bg-n-background overflow-hidden">
    <!-- Header Row -->
    <div class="flex items-center h-14 px-4 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-n-slate-3">
      <!-- DESABILITADO: Menu sempre aberto -->
      <!-- <div v-if="showSidemenuIcon" class="rounded-lg outline-1 outline outline-n-container dark:bg-n-slate-1 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200">
        <Button
          icon="i-lucide-menu"
          size="sm"
          color="slate"
          class="!outline-0 dark:!bg-transparent hover:!bg-transparent"
          @click="onSidemenuClick"
        />
      </div> -->
      <!-- Navigation arrows -->
      <div class="flex items-center gap-2 ml-2">
        <div class="rounded-lg outline-1 outline outline-n-container dark:bg-n-slate-1 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200">
          <Button
            icon="i-lucide-chevron-left"
            size="sm"
            color="slate"
            class="!outline-0 dark:!bg-transparent hover:!bg-transparent"
            @click="navigatePrev"
          />
        </div>
        <div class="rounded-lg outline-1 outline outline-n-container dark:bg-n-slate-1 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200">
          <Button
            icon="i-lucide-chevron-right"
            size="sm"
            color="slate"
            class="!outline-0 dark:!bg-transparent hover:!bg-transparent"
            @click="navigateNext"
          />
        </div>
      </div>
      <h1 class="text-lg font-medium text-slate-900 dark:text-slate-100 ml-2">{{ currentMonthYear }}</h1>

      <!-- Right side controls (moved from sidebar header) -->
      <div class="flex items-center gap-2 ml-auto">
        <!-- Connection status dropdown -->
        <div class="relative">
          <div class="rounded-lg outline-1 outline outline-n-container dark:bg-n-slate-1 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200">
            <Button
              :label="connectionLabel"
              size="sm"
              color="slate"
              class="ltr:rounded-r-none rtl:rounded-l-none !outline-0 flex-1 !bg-transparent hover:!bg-transparent"
              @click="openConnectionDropdown"
            />
            <Button
              icon="i-lucide-chevron-down"
              size="sm"
              color="slate"
              class="ltr:rounded-l-none rtl:rounded-r-none !outline-0 aspect-square !bg-transparent hover:!bg-transparent"
              trailing-icon
              @click="openConnectionDropdown"
            />
          </div>
          <div
            v-if="showConnectionDropdown"
            v-on-clickaway="closeConnectionDropdown"
            class="dropdown-pane dropdown-pane--open left-auto top-full mt-0.5 ltr:right-0 rtl:left-0 max-w-[12.5rem] min-w-[9.75rem]"
          >
            <WootDropdownMenu class="mb-0">
              <WootDropdownItem v-if="calendarConnected">
                <Button
                  label="Atualizar"
                  ghost
                  slate
                  sm
                  start
                  icon="i-lucide-refresh-cw"
                  class="w-full"
                  @click="() => handleUpdateConnection()"
                />
              </WootDropdownItem>
              <WootDropdownItem v-if="!calendarConnected">
                <Button
                  label="Conectar"
                  ghost
                  slate
                  sm
                  start
                  icon="i-lucide-link"
                  class="w-full"
                  @click="() => handleConnect()"
                />
              </WootDropdownItem>
              <WootDropdownItem v-if="calendarConnected">
                <Button
                  label="Desconectar"
                  ghost
                  ruby
                  sm
                  start
                  icon="i-lucide-log-out"
                  class="w-full"
                  @click="() => handleDisconnect()"
                />
              </WootDropdownItem>
            </WootDropdownMenu>
          </div>
        </div>
        <!-- Connection status indicator -->
        <div class="rounded-lg outline-1 outline outline-n-container py-2.5 px-2.5 flex items-center justify-center dark:bg-n-slate-1">
          <div v-if="calendarConnected" class="rounded-full w-3 h-3 bg-grass-500"></div>
          <div v-else class="rounded-full w-3 h-3 bg-red-500"></div>
        </div>
        <!-- Sidebar toggle button -->
        <div class="rounded-lg outline-1 outline outline-n-container dark:bg-n-slate-1 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200">
          <Button
            :icon="contactPanelIcon"
            size="sm"
            color="slate"
            class="!outline-0 dark:!bg-transparent hover:!bg-transparent"
            @click="toggleSidebar"
          />
        </div>
      </div>
    </div>

    <!-- Content Row (Calendar + Sidebar) -->
    <div class="flex flex-1 overflow-hidden">
      <!-- Main Content Area -->
      <div class="flex-1 flex flex-col overflow-hidden">

        <!-- Calendar wrapper -->
        <div class="calendar-wrapper relative bg-n-slate-1 dark:bg-n-slate-2 flex-1 flex flex-col overflow-auto h-fit">
          <FullCalendar ref="calendar" :options="calendarOptions" />

        <!-- Event Tooltip -->
        <div
          v-if="hoveredEvent"
          ref="tooltip"
          class="fixed z-50 bg-white dark:bg-slate-900 border border-n-slate-3 shadow-lg rounded-lg max-w-xs min-w-[250px] p-4 pointer-events-none overflow-hidden"
          :style="tooltipStyle"
        >
          <!-- Responza badge -->
          <div v-if="hoveredEvent.extendedProps?.professional_id" class="mb-1.5 text-sm text-n-brand font-medium">
            Criado pelo Responza
          </div>

          <!-- Header with professional circle and title -->
          <div class="flex gap-3 items-center">
            <div
              class="w-3 h-3 rounded-full flex-shrink-0"
              :style="{ backgroundColor: getProfessionalColor(hoveredEvent.extendedProps?.professional_id) }"
            ></div>
            <div class="flex-1">
              <div class="font-semibold text-base text-n-slate-12 leading-none">{{ hoveredEvent.title }}</div>
            </div>
          </div>

          <!-- Date and time -->
          <div v-if="hoveredEvent.start" class="mt-1.5 text-sm text-n-slate-10">
            <div>{{ formatTooltipDateOnly(hoveredEvent.start) }}</div>
            <div>{{ formatTooltipTimeOnly(hoveredEvent.start, hoveredEvent.end) }}</div>
          </div>

          <!-- Professional info -->
          <div v-if="hoveredEvent.extendedProps?.professional_id" class="mt-4">
            <div class="text-xs text-n-slate-10 mb-1">Profissional</div>
            <div class="text-sm text-n-slate-12 font-medium">
              {{ getProfessionalNameById(hoveredEvent.extendedProps.professional_id) }}
            </div>
          </div>

          <!-- Description -->
          <div v-if="hoveredEvent.extendedProps?.description" class="mt-4">
            <div class="text-xs text-n-slate-10 mb-1">Descrição</div>
            <div class="text-sm text-n-slate-12 font-medium break-all">{{ truncateDescription(hoveredEvent.extendedProps.description) }}</div>
          </div>

          <!-- Client info -->
          <div v-if="hoveredEvent.extendedProps?.client_name" class="mt-4">
            <div class="text-xs text-n-slate-10 mb-1">Cliente</div>
            <div class="text-sm text-n-slate-12 font-medium">{{ hoveredEvent.extendedProps.client_name }}</div>
          </div>

        </div>

        <!-- States overlays -->
        <transition name="fade">
          <div v-if="error" class="absolute inset-0 flex flex-col items-center justify-center z-10 error-overlay">
            <div class="bg-white dark:bg-slate-900 p-6 mb-20 mx-auto shadow-lg text-center text-n-slate-12 flex flex-col items-center gap-4 max-w-[80%] w-96 rounded-lg border border-n-slate-3">
              <span>{{ error }}</span>
              <div class="flex flex-col gap-3 mt-2 w-full max-w-xs">
                <button @click="retryFetch" class="py-3 px-5 rounded-lg border-none font-medium cursor-pointer transition-all duration-200 text-center no-underline inline-block bg-n-brand text-white">
                  Tentar novamente
                </button>
                <router-link v-if="isConnectionError"
                  :to="`/app/accounts/${accountId}/settings/integrations/googleCalendar`"
                  class="py-3 px-5 rounded-lg border-none font-medium cursor-pointer transition-all duration-200 text-center no-underline inline-block bg-n-brand text-white">
                  Configurar Agenda
                </router-link>
              </div>
            </div>
          </div>
        </transition>

        <!-- Notification when trying to add event without calendar -->
        <transition name="fade">
          <div v-if="showCalendarAlert" class="absolute inset-0 flex flex-col items-center justify-start z-10 alert-overlay">
            <div class="bg-white dark:bg-slate-900 p-6 mt-20 mx-auto shadow-lg text-center text-n-slate-12 flex flex-col items-center gap-4 max-w-[80%] w-96 rounded-lg border border-n-slate-3">
              <div class="w-full flex justify-between items-center border-b border-n-slate-3 pb-2">
                <h3 class="m-0 text-xl font-medium">Agenda não conectada</h3>
                <button class="bg-none border-none text-2xl text-n-slate-11 cursor-pointer p-0 leading-none hover:text-n-slate-12" @click="showCalendarAlert = false">
                  &times;
                </button>
              </div>
              <p>Você precisa conectar sua Agenda do Google para adicionar eventos.</p>
              <div class="flex flex-col gap-3 mt-2 w-full max-w-xs">
                <router-link :to="`/app/accounts/${accountId}/settings/integrations/googleCalendar`"
                  class="py-3 px-5 rounded-lg border-none font-medium cursor-pointer transition-all duration-200 text-center no-underline inline-block bg-n-brand text-white">
                  Configurar Agenda
                </router-link>
                <button @click="showCalendarAlert = false" class="py-3 px-5 rounded-lg border-none font-medium cursor-pointer transition-all duration-200 text-center no-underline inline-block bg-n-slate-2 text-n-slate-11 hover:bg-n-slate-3">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <!-- Event Modal -->
      <EventModal v-if="showEventModal" :show="showEventModal" :event="selectedEvent" :selectedDate="selectedDate"
        :selectedEndDate="selectedEndDate" @close="closeEventModal" @submit="handleEventSaved"
        @delete="handleEventDeleted" />
    </div>

    <!-- Right Sidebar -->
    <div :class="['transition-all duration-300 ease-in-out bg-slate-50 border-l border-slate-100 flex flex-col dark:bg-slate-900 dark:border-slate-700', sidebarCollapsed ? 'w-16' : 'w-80']">
      <!-- Sidebar Content -->
      <div class="flex-1 overflow-visible">

        <!-- Navigation Controls -->
        <div v-if="calendarConnected" class="transition-colors">
          <!-- Content -->
          <div class="overflow-visible">
            <div v-if="!sidebarCollapsed" class="px-4 py-4 space-y-2">
              <!-- Ações section title -->
              <div class="flex items-center gap-2">
                <span class="i-lucide-zap w-4 h-4 text-slate-500 dark:text-slate-500"></span>
                <span class="text-xs font-medium text-slate-500 dark:text-slate-500 uppercase tracking-wide">Ações</span>
              </div>

              <!-- New Event button -->
              <button
                class="w-full text-slate-500 dark:text-white border border-slate-200 dark:border-transparent rounded-lg py-2.5 px-4 text-sm font-medium cursor-pointer inline-flex items-center justify-start gap-2 transition-colors duration-200 bg-white hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-white"
                @click="handleAddEvent">
                <span class="i-lucide-plus w-4 h-4"></span>
                <span>Novo</span>
              </button>

              <!-- visualization -->
              <div class="relative">
                <button 
                  :class="[
                    'w-full border rounded-lg py-2.5 px-4 text-sm font-medium cursor-pointer inline-flex items-center justify-start gap-2 transition-colors duration-200',
                    'text-slate-500 dark:text-white border-slate-200 dark:border-transparent bg-white hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-white'
                  ]"
                  @click="openViewDropdown">
                  <span :class="selectedViewIcon + ' w-4 h-4'"></span>
                  <span>{{ selectedViewLabel }}</span>
                  <span class="i-lucide-chevron-down w-4 h-4 ml-auto"></span>
                </button>
                <div
                  v-if="showViewDropdown"
                  v-on-clickaway="closeViewDropdown"
                  class="dropdown-pane dropdown-pane--open left-auto top-full ltr:right-0 rtl:left-0 max-w-[20rem] min-w-[12rem] z-50"
                  style="overflow: visible;"
                >
                  <WootDropdownMenu class="mb-0 z-50">
                    <WootDropdownItem>
                      <Button
                        label="Mês"
                        ghost
                        slate
                        sm
                        start
                        icon="i-lucide-calendar"
                        class="w-full"
                        @click="() => selectView('dayGridMonth', 'Mês')"
                      />
                    </WootDropdownItem>
                    <WootDropdownItem>
                      <Button
                        label="Semana"
                        ghost
                        slate
                        sm
                        start
                        icon="i-lucide-columns-3"
                        class="w-full"
                        @click="() => selectView('dayGridWeek', 'Semana')"
                      />
                    </WootDropdownItem>
                    <WootDropdownItem>
                      <Button
                        label="Dia"
                        ghost
                        slate
                        sm
                        start
                        icon="i-lucide-square"
                        class="w-full"
                        @click="() => selectView('dayGridDay', 'Dia')"
                      />
                    </WootDropdownItem>
                    <WootDropdownItem>
                      <Button
                        label="Lista"
                        ghost
                        slate
                        sm
                        start
                        icon="i-lucide-list"
                        class="w-full"
                        @click="() => selectView('listMonth', 'Lista')"
                      />
                    </WootDropdownItem>
                  </WootDropdownMenu>
                </div>
              </div>

              <!-- date input field -->
              <div class="relative">
                <input 
                  v-model="dateInputValue"
                  @keyup.enter="handleDateInput"
                  type="text"
                  placeholder="Digite uma data (ex: 2024-12-25)"
                  class="w-full text-slate-700 dark:text-white border border-slate-200 dark:border-slate-600 rounded-lg py-2.5 px-4 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent placeholder-slate-400 dark:placeholder-slate-300 dark:bg-slate-800 !mb-0"
                />
              </div>

              <!-- today button -->
              <button
                ref="todayButton"
                class="w-full text-slate-500 dark:text-white border border-slate-200 dark:border-transparent rounded-lg py-2.5 px-4 text-sm font-medium cursor-pointer inline-flex items-center justify-start gap-2 transition-colors duration-200 bg-white hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-white"
                @click="navigateToday">
                <span class="i-lucide-calendar-clock w-4 h-4"></span>
                <span>Data Atual</span>
              </button>
            </div>

            <!-- Filtros section (expanded) -->
            <div v-if="!sidebarCollapsed" class="px-4 py-4 space-y-2">
              <!-- Filtros section title -->
              <div class="flex items-center gap-2">
                <span class="i-lucide-list-filter w-4 h-4 text-slate-500 dark:text-slate-500"></span>
                <span class="text-xs font-medium text-slate-500 dark:text-slate-500 uppercase tracking-wide">Filtros</span>
              </div>

              <!-- Professional filter -->
              <div class="relative">
                <button
                  :class="[
                    'w-full border rounded-lg py-2.5 px-4 text-sm font-medium cursor-pointer inline-flex items-center justify-start gap-2 transition-colors duration-200',
                    selectedProfessionalId !== 'all'
                      ? 'bg-n-brand text-white border-n-brand hover:bg-n-brand-dark'
                      : 'text-slate-500 dark:text-white border-slate-200 dark:border-transparent bg-white hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-white'
                  ]"
                  @click="openProfessionalDropdown">
                  <span>{{ selectedProfessionalLabel }}</span>
                  <span class="i-lucide-chevron-down w-4 h-4 ml-auto"></span>
                </button>
                <div
                  v-if="showProfessionalDropdown"
                  v-on-clickaway="closeProfessionalDropdown"
                  class="dropdown-pane dropdown-pane--open left-auto top-full mb-0.5 ltr:right-0 rtl:left-0 max-w-[20rem] min-w-[12rem] z-50"
                  style="overflow: visible;"
                >
                  <WootDropdownMenu class="mb-0 z-50">
                    <WootDropdownItem>
                      <Button
                        label="Todos"
                        ghost
                        slate
                        sm
                        start
                        icon="i-lucide-users"
                        class="w-full"
                        @click="() => selectProfessional('all')"
                      />
                    </WootDropdownItem>
                    <WootDropdownItem v-for="professional in professionals" :key="professional.id">
                      <Button
                        :label="professional.name"
                        ghost
                        slate
                        sm
                        start
                        icon="i-lucide-user"
                        class="w-full"
                        @click="() => selectProfessional(professional.id)"
                      />
                    </WootDropdownItem>
                  </WootDropdownMenu>
                </div>
              </div>

              <!-- Criados pelo Responza filter -->
              <label
                class="w-full border rounded-lg py-2.5 px-4 text-sm font-medium cursor-pointer inline-flex items-center justify-start gap-2 transition-colors duration-200 text-slate-500 dark:text-white border-slate-200 dark:border-transparent bg-white hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-white">
                <input
                  type="checkbox"
                  v-model="showOnlyResponzaEvents"
                  @change="onResponzaFilterChange"
                  class="w-4 h-4 rounded border-slate-300 accent-n-brand"
                />
                <span>Criados pelo Responza</span>
              </label>
            </div>

            <!-- Collapsed state buttons -->
            <div v-if="sidebarCollapsed" class="flex flex-col items-center space-y-2 px-2 py-4 border-t border-slate-200 dark:border-slate-800">
              <!-- New Event button -->
              <button
                class="w-10 h-10 text-slate-500 dark:text-white border border-slate-200 dark:border-transparent rounded-lg text-sm font-medium cursor-pointer inline-flex items-center justify-center transition-colors duration-200 bg-white hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-white"
                @click="handleAddEvent"
                title="Novo">
                <span class="i-lucide-plus w-4 h-4"></span>
              </button>

              <div class="relative">
                <button
                  ref="collapsedViewButton"
                  class="w-10 h-10 text-slate-500 dark:text-white border border-slate-200 dark:border-transparent rounded-lg text-sm font-medium cursor-pointer inline-flex items-center justify-center transition-colors duration-200 bg-white hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-white"
                  @click="openViewDropdown"
                  title="Visualização">
                  <span :class="selectedViewIcon + ' w-4 h-4'"></span>
                </button>
                <div
                  v-if="showViewDropdown && sidebarCollapsed"
                  v-on-clickaway="closeViewDropdown"
                  class="dropdown-pane dropdown-pane--open right-full top-0 mr-0.5 max-w-[20rem] min-w-[12rem] z-50"
                  style="overflow: visible;"
                >
                  <WootDropdownMenu class="mb-0 z-50">
                    <WootDropdownItem>
                      <Button
                        label="Mês"
                        ghost
                        slate
                        sm
                        start
                        icon="i-lucide-calendar"
                        class="w-full"
                        @click="() => selectView('dayGridMonth', 'Mês')"
                      />
                    </WootDropdownItem>
                    <WootDropdownItem>
                      <Button
                        label="Semana"
                        ghost
                        slate
                        sm
                        start
                        icon="i-lucide-columns-3"
                        class="w-full"
                        @click="() => selectView('dayGridWeek', 'Semana')"
                      />
                    </WootDropdownItem>
                    <WootDropdownItem>
                      <Button
                        label="Dia"
                        ghost
                        slate
                        sm
                        start
                        icon="i-lucide-square"
                        class="w-full"
                        @click="() => selectView('dayGridDay', 'Dia')"
                      />
                    </WootDropdownItem>
                    <WootDropdownItem>
                      <Button
                        label="Lista"
                        ghost
                        slate
                        sm
                        start
                        icon="i-lucide-list"
                        class="w-full"
                        @click="() => selectView('listMonth', 'Lista')"
                      />
                    </WootDropdownItem>
                  </WootDropdownMenu>
                </div>
              </div>

              <button
                ref="collapsedTodayButton"
                class="w-10 h-10 text-slate-500 dark:text-white border border-slate-200 dark:border-transparent rounded-lg text-sm font-medium cursor-pointer inline-flex items-center justify-center transition-colors duration-200 bg-white hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-white"
                @click="navigateToday"
                title="Hoje">
                <span class="i-lucide-calendar-clock w-4 h-4"></span>
              </button>
            </div>

            <!-- Collapsed Filtros section -->
            <div v-if="sidebarCollapsed" class="flex flex-col items-center space-y-2 px-2 py-4 border-t border-slate-200 dark:border-slate-800">
              <!-- Professional filter -->
              <div class="relative">
                <button
                  ref="collapsedFilterButton"
                  :class="[
                    'w-10 h-10 border rounded-lg text-sm font-medium cursor-pointer inline-flex items-center justify-center transition-colors duration-200',
                    selectedProfessionalId !== 'all'
                      ? 'bg-n-brand text-white border-n-brand hover:bg-n-brand-dark'
                      : 'text-slate-500 dark:text-white border-slate-200 dark:border-transparent bg-white hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-white'
                  ]"
                  @click="openProfessionalDropdown"
                  title="Filtrar por Profissional">
                  <span class="i-lucide-user w-4 h-4"></span>
                </button>
                <div
                  v-if="showProfessionalDropdown && sidebarCollapsed"
                  v-on-clickaway="closeProfessionalDropdown"
                  class="dropdown-pane dropdown-pane--open right-full top-0 mr-0.5 max-w-[20rem] min-w-[12rem] z-50"
                  style="overflow: visible;"
                >
                  <WootDropdownMenu class="mb-0 z-50">
                    <WootDropdownItem>
                      <Button
                        label="Todos"
                        ghost
                        slate
                        sm
                        start
                        icon="i-lucide-users"
                        class="w-full"
                        @click="() => selectProfessional('all')"
                      />
                    </WootDropdownItem>
                    <WootDropdownItem v-for="professional in professionals" :key="professional.id">
                      <Button
                        :label="professional.name"
                        ghost
                        slate
                        sm
                        start
                        icon="i-lucide-user"
                        class="w-full"
                        @click="() => selectProfessional(professional.id)"
                      />
                    </WootDropdownItem>
                  </WootDropdownMenu>
                </div>
              </div>

              <!-- Criados pelo Responza filter -->
              <button
                :class="[
                  'w-10 h-10 border rounded-lg text-sm font-medium cursor-pointer inline-flex items-center justify-center transition-colors duration-200',
                  showOnlyResponzaEvents
                    ? 'bg-n-brand text-white border-n-brand hover:bg-n-brand-dark'
                    : 'text-slate-500 dark:text-white border-slate-200 dark:border-transparent bg-white hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-white'
                ]"
                @click="toggleResponzaFilter"
                title="Criados pelo Responza">
                <span class="i-lucide-filter w-4 h-4"></span>
              </button>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="transition-colors">
          <!-- Content -->
          <div class="overflow-visible">
            <div v-if="!sidebarCollapsed" class="px-4 py-4 space-y-2">
              <!-- Configurações section title -->
              <div class="flex items-center gap-2">
                <span class="i-lucide-settings w-4 h-4 text-slate-500 dark:text-slate-500"></span>
                <span class="text-xs font-medium text-slate-500 dark:text-slate-500 uppercase tracking-wide">Configurações</span>
              </div>

              <router-link
                :to="`/app/accounts/${accountId}/settings/integrations/googleCalendar`"
                class="w-full text-slate-500 dark:text-white border border-slate-200 dark:border-transparent rounded-lg py-2.5 px-4 text-sm font-medium cursor-pointer inline-flex items-center justify-start gap-2 transition-colors duration-200 bg-white hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-white no-underline">
                <span>Agenda</span>
              </router-link>

              <!-- Debug Mode: Copy Google Token -->
              <button
                v-if="isDebugMode && calendarConnected"
                class="w-full text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-600 rounded-lg py-2.5 px-4 text-sm font-medium cursor-pointer inline-flex items-center justify-start gap-2 transition-colors duration-200 bg-amber-50 dark:bg-amber-950 hover:bg-amber-100 dark:hover:bg-amber-900"
                @click="copyGoogleToken">
                <span class="i-lucide-terminal w-4 h-4"></span>
                <span>Debug: Copiar Token</span>
              </button>
            </div>
            
            <!-- Collapsed state buttons -->
            <div v-if="sidebarCollapsed" class="flex flex-col items-center space-y-2 px-2 py-4 border-t border-slate-200 dark:border-slate-800">
              <router-link
                :to="`/app/accounts/${accountId}/settings/integrations/googleCalendar`"
                class="w-10 h-10 text-slate-500 dark:text-white border border-slate-200 dark:border-transparent rounded-lg text-sm font-medium cursor-pointer inline-flex items-center justify-center transition-colors duration-200 bg-white hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-white no-underline"
                title="Configurações">
                <span class="i-lucide-settings w-4 h-4"></span>
              </router-link>

              <!-- Debug Mode: Copy Google Token (collapsed) -->
              <button
                v-if="isDebugMode && calendarConnected"
                class="w-10 h-10 text-amber-600 dark:text-amber-400 border border-amber-300 dark:border-amber-600 rounded-lg text-sm font-medium cursor-pointer inline-flex items-center justify-center transition-colors duration-200 bg-amber-50 dark:bg-amber-950 hover:bg-amber-100 dark:hover:bg-amber-900"
                @click="copyGoogleToken"
                title="Debug: Copiar Token do Google">
                <span class="i-lucide-terminal w-4 h-4"></span>
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
    <!-- End Content Row -->
    </div>

  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import ptBr from '@fullcalendar/core/locales/pt-br'
import axios from 'axios'
import EventModal from './components/EventModal.vue'
import Button from 'dashboard/components-next/button/Button.vue'
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue'
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue'
import ContactInfoDropdown from 'dashboard/components/ContactInfoDropdown.vue'
import ContactInfoItem from 'dashboard/components/ContactInfoItem.vue'
import { emitter } from 'shared/helpers/mitt'
import { BUS_EVENTS } from 'shared/constants/busEvents'

export default {
  name: 'AppointmentsIndex',
  components: {
    FullCalendar,
    EventModal,
    Button,
    WootDropdownItem,
    WootDropdownMenu,
    ContactInfoDropdown,
    ContactInfoItem
  },
  data() {
    return {
      // UI state
      loading: false,
      loadingMessage: 'Carregando...',
      error: null,
      isConnectionError: false,
      showCalendarAlert: false,
      currentView: 'dayGridMonth',
      currentCalendarTitle: '',
      showEventModal: false,
      selectedEvent: null,
      selectedDate: null,
      selectedEndDate: null,
      calendarConnected: false,
      connectedEmail: null,
      refreshTimer: null,
      sidebarCollapsed: false,
      showConnectionDropdown: false,
      dateInputValue: '',
      selectedProfessionalId: 'all',
      professionals: [],
      showProfessionalDropdown: false,
      showViewDropdown: false,
      selectedViewLabel: 'Mês',
      showOnlyResponzaEvents: false,

      // Tooltip state
      hoveredEvent: null,
      tooltipStyle: {},


      // Debug mode state
      isDebugMode: false,

      // Current calendar date for header display
      currentCalendarDate: new Date(),

      // Calendar configuration
      calendarOptions: {
        plugins: [dayGridPlugin, interactionPlugin, listPlugin],
        initialView: 'dayGridMonth',
        initialDate: new Date(),
        locale: ptBr,
        headerToolbar: false,
        height: "auto",
        contentHeight: "auto",
        aspectRatio: 1.35,
        stickyHeaderDates: true,
        eventBackgroundColor: '#3788d8',
        eventBorderColor: '#3788d8',
        eventTextColor: '#ffffff',
        eventTimeFormat: {
          hour: '2-digit',
          minute: '2-digit',
          meridiem: false,
          hour12: false
        },
        forceEventDuration: false,
        eventDisplay: 'auto',
        datesSet: null, // Set in mounted()
        rerenderDelay: 50,
        events: this.fetchCalendarEvents,
        eventClick: this.handleEventClick,
        eventMouseEnter: this.handleEventMouseEnter,
        eventMouseLeave: this.handleEventMouseLeave,
        editable: false, // Disable drag and drop
        selectable: false, // Disable date selection
      }
    }
  },
  computed: {
    accountId() {
      const segments = window.location.pathname.split('/');
      const accountIndex = segments.findIndex(segment => segment === 'accounts');
      return accountIndex >= 0 && segments.length > accountIndex + 1 ? segments[accountIndex + 1] : null;
    },
    contactPanelIcon() {
      return this.sidebarCollapsed ? 'i-lucide-arrow-left-to-line' : 'i-lucide-arrow-right-to-line';
    },
    contactPanelLabel() {
      return this.sidebarCollapsed ? 'Expandir' : '';
    },
    selectedProfessionalLabel() {
      if (this.selectedProfessionalId === 'all') {
        return 'Todos profissionais';
      }
      const professional = this.professionals.find(p => p.id === this.selectedProfessionalId);
      return professional ? professional.name : 'Selecionar';
    },
    selectedViewIcon() {
      switch (this.currentView) {
        case 'dayGridMonth':
          return 'i-lucide-calendar';
        case 'dayGridWeek':
          return 'i-lucide-columns-3';
        case 'dayGridDay':
          return 'i-lucide-square';
        case 'listMonth':
          return 'i-lucide-list';
        default:
          return 'i-lucide-eye';
      }
    },
    currentMonthYear() {
      const months = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ];
      const date = this.currentCalendarDate;
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      return `${month} ${year}`;
    },
    connectionLabel() {
      if (!this.calendarConnected) {
        return 'Desconectado';
      }
      if (this.connectedEmail) {
        return this.connectedEmail;
      }
      return 'Conectado';
    },
  },
  methods: {
    // Handle calendar date changes to update header
    handleDatesSet(dateInfo) {
      this.currentCalendarDate = dateInfo.view.currentStart;
    },

    // Method to refresh the calendar
    refreshCalendar() {
      if (this.$refs.calendar) {
        const calendarApi = this.$refs.calendar.getApi();
        calendarApi.refetchEvents();
        // console.log('Calendar refreshed at', new Date().toLocaleTimeString());
      }
    },

    // Round to the next hour
    roundToNextHour(date) {
      const roundedDate = new Date(date);
      roundedDate.setHours(roundedDate.getHours() + 1);
      roundedDate.setMinutes(0);
      roundedDate.setSeconds(0);
      roundedDate.setMilliseconds(0);
      return roundedDate;
    },

    // Calculate the end date (1 hour after start)
    calculateEndDate(startDate) {
      const endDate = new Date(startDate);
      endDate.setHours(endDate.getHours() + 1);
      return endDate;
    },

    // Get authentication headers from cookie
    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    },
    getAuthHeaders() {
      const cookie = this.getCookie('cw_d_session_info');
      if (!cookie) {
        console.error('cw_d_session_info cookie não encontrado');
        return {};
      }
      try {
        const sessionInfo = JSON.parse(decodeURIComponent(cookie));
        return {
          'access-token': sessionInfo['access-token'],
          client: sessionInfo.client,
          uid: sessionInfo.uid,
        };
      } catch (e) {
        console.error('Erro ao analisar cw_d_session_info:', e);
        return {};
      }
    },

    // Handle add event button click
    handleAddEvent() {
      if (this.calendarConnected) {
        // Round to the next hour
        const currentDate = new Date();
        this.selectedDate = this.roundToNextHour(currentDate);

        // Set end date to 1 hour after start
        this.selectedEndDate = this.calculateEndDate(this.selectedDate);

        this.selectedEvent = null;
        this.showEventModal = true;
      } else {
        // If calendar is not connected, show alert
        this.showCalendarAlert = true;
      }
    },

    // Handle event click
    handleEventClick(clickInfo) {
      // Only open edit modal if calendar is connected
      if (this.calendarConnected) {
        this.selectedEvent = clickInfo.event;
        this.selectedDate = null;
        this.selectedEndDate = null;
        this.showEventModal = true;
      } else {
        // Show connection alert if calendar is not connected
        this.showCalendarAlert = true;
      }
    },

    // Close event modal
    closeEventModal() {
      this.showEventModal = false;
      this.selectedEvent = null;
      this.selectedDate = null;
      this.selectedEndDate = null;
    },

    // Handle event saved from modal
    handleEventSaved(eventData) {
      // console.log('Event saved:', eventData);

      // Only update existing events in the calendar, new events will be fetched via refresh
      const calendarApi = this.$refs.calendar.getApi();

      if (this.selectedEvent && this.selectedEvent.id) {
        // Update existing event
        const existingEvent = calendarApi.getEventById(this.selectedEvent.id);

        if (existingEvent) {
          existingEvent.setProp('title', eventData.title);
          existingEvent.setStart(eventData.start);
          existingEvent.setEnd(eventData.end);
          existingEvent.setAllDay(eventData.allDay || false);

          // Update extended props
          if (eventData.extendedProps) {
            Object.entries(eventData.extendedProps).forEach(([key, value]) => {
              existingEvent.setExtendedProp(key, value);
            });
          }
        }
      }

      // Close the modal
      this.closeEventModal();

      // Refresh the calendar to fetch all events from server (including new ones)
      this.refreshCalendar();
    },

    // Handle event deleted from modal
    handleEventDeleted(eventId) {
      // console.log('Event deleted:', eventId);

      // Remove the event from the calendar
      const calendarApi = this.$refs.calendar.getApi();
      const existingEvent = calendarApi.getEventById(eventId);

      if (existingEvent) {
        existingEvent.remove();
      }

      // Close the modal
      this.closeEventModal();

      // Refresh the calendar to ensure consistency
      this.refreshCalendar();
    },

    // Fetch events directly from Google Calendar API endpoint
    async fetchCalendarEvents(fetchInfo, successCallback, failureCallback) {
      this.loading = true;
      this.loadingMessage = 'Carregando eventos...';
      this.error = null;
      this.isConnectionError = false;

      try {
        // Get authentication headers
        const headers = this.getAuthHeaders();

        // Make API call to backend
        const response = await axios.get('/api/v1/google_calendar', {
          headers,
          withCredentials: true,
          params: {
            start: fetchInfo.startStr,
            end: fetchInfo.endStr
          }
        });

        // Process response
        let eventsArray = [];
        if (response.data && Array.isArray(response.data)) {
          this.calendarConnected = true;
          eventsArray = response.data;
        } else if (response.data && response.data.events && Array.isArray(response.data.events)) {
          this.calendarConnected = true;
          eventsArray = response.data.events;
        } else {
          // Empty or unexpected response format
          // console.warn('Resposta inesperada do servidor:', response.data);
          this.calendarConnected = !!response.data;
          eventsArray = [];
        }

        // Apply professional filter
        if (this.selectedProfessionalId !== 'all') {
          const chosenId = this.selectedProfessionalId.toString();
          eventsArray = eventsArray.filter(ev => {
            return (
              ev.extendedProps &&
              ev.extendedProps.professional_id &&
              ev.extendedProps.professional_id.toString() === chosenId
            );
          });
        }

        // Apply Responza-only filter
        if (this.showOnlyResponzaEvents) {
          eventsArray = eventsArray.filter(ev => {
            return ev.extendedProps && ev.extendedProps.professional_id;
          });
        }

        // Apply professional colors to events
        eventsArray = eventsArray.map(event => {
          let professionalColor;
          if (event.extendedProps && event.extendedProps.professional_id) {
            professionalColor = this.getProfessionalColor(event.extendedProps.professional_id);
          } else {
            // Events without professionals should show gray
            professionalColor = '#9ca3af';
          }
          
          return {
            ...event,
            backgroundColor: professionalColor,
            borderColor: professionalColor,
            textColor: '#ffffff'
          };
        });

        successCallback(eventsArray);
      } catch (err) {
        //console.error('Falha ao carregar eventos:', err);
        this.calendarConnected = false;

        // Format error message
        let errorMessage = 'Erro ao carregar eventos. Tente novamente.';
        this.isConnectionError = false;

        if (err.response) {
          if (err.response.status === 401) {
            if (err.response.data && err.response.data.error &&
              (err.response.data.error.includes('Google Calendar não está conectado') ||
                err.response.data.error.includes('Agenda do Google não está conectada'))) {
              errorMessage = 'Agenda do Google não está conectada. Visite a página de configurações para conectá-la.';
              this.isConnectionError = true;
            } else {
              errorMessage = 'Sessão expirada ou usuário não autenticado. Faça login novamente.';
            }
          } else if (err.response.status === 403) {
            errorMessage = 'Agenda do Google não está conectada. Visite a página de configurações para conectá-la.';
            this.isConnectionError = true;
          } else if (err.response.data && err.response.data.error) {
            // Use error message from the API response if available
            if (err.response.data.error === 'token_expired') {
              errorMessage = 'Token do Google Calendar expirado. Atualize as permissões nas configurações.';
              this.isConnectionError = true;
            } else if (err.response.data.error.includes('Google Calendar não está conectado') ||
              err.response.data.error.includes('Agenda do Google não está conectada')) {
              errorMessage = err.response.data.error;
              this.isConnectionError = true;
            } else {
              errorMessage = err.response.data.error;
              // Check if the error is related to connection or permission
              if (err.response.data.error.includes('token') ||
                err.response.data.error.includes('conecte') ||
                err.response.data.error.includes('autorização')) {
                this.isConnectionError = true;
              }
            }
          }
        } else if (err.request) {
          errorMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão.';
        }

        this.error = errorMessage;
        failureCallback(err);
      } finally {
        this.loading = false;
      }
    },

    // Change calendar view
    changeView(viewName) {
      const calendarApi = this.$refs.calendar.getApi();
      calendarApi.changeView(viewName);
      this.currentView = viewName;

      // Wait for FullCalendar to render new events
      this.$nextTick(() => {
        calendarApi.refetchEvents();
      });
    },


    // Format date
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    // Retry loading events
    retryFetch() {
      this.error = null;
      if (this.$refs.calendar) {
        const calendarApi = this.$refs.calendar.getApi();
        calendarApi.refetchEvents();
      }
    },

    // Handle date changes
    onDatesSet(dateInfo) {
      // Handle any date change logic here if needed
    },

    // Navigation methods
    navigatePrev() {
      const calendarApi = this.$refs.calendar.getApi();
      calendarApi.prev();
    },

    navigateToday() {
      const calendarApi = this.$refs.calendar.getApi();
      calendarApi.gotoDate(new Date());
      
      // Add click effect - temporarily change hover color to blue
      if (this.$refs.todayButton) {
        const button = this.$refs.todayButton;
        // Remove original hover classes
        button.classList.remove('hover:bg-slate-100', 'dark:hover:bg-slate-700', 'hover:text-slate-700', 'dark:hover:text-white');
        // Add blue hover classes
        button.classList.add('hover:bg-n-brand', 'hover:text-white', 'hover:border-n-brand', 'dark:hover:bg-n-brand', 'dark:hover:text-white', 'dark:hover:border-n-brand');
        
        setTimeout(() => {
          // Restore original hover classes
          button.classList.remove('hover:bg-n-brand', 'hover:text-white', 'hover:border-n-brand', 'dark:hover:bg-n-brand', 'dark:hover:text-white', 'dark:hover:border-n-brand');
          button.classList.add('hover:bg-slate-100', 'dark:hover:bg-slate-700', 'hover:text-slate-700', 'dark:hover:text-white');
        }, 300);
      }
      
      // Add click effect for collapsed button as well
      if (this.$refs.collapsedTodayButton) {
        const button = this.$refs.collapsedTodayButton;
        // Remove original hover classes
        button.classList.remove('hover:bg-slate-100', 'dark:hover:bg-slate-700', 'hover:text-slate-700', 'dark:hover:text-white');
        // Add blue hover classes
        button.classList.add('hover:bg-n-brand', 'hover:text-white', 'hover:border-n-brand', 'dark:hover:bg-n-brand', 'dark:hover:text-white', 'dark:hover:border-n-brand');
        
        setTimeout(() => {
          // Restore original hover classes
          button.classList.remove('hover:bg-n-brand', 'hover:text-white', 'hover:border-n-brand', 'dark:hover:bg-n-brand', 'dark:hover:text-white', 'dark:hover:border-n-brand');
          button.classList.add('hover:bg-slate-100', 'dark:hover:bg-slate-700', 'hover:text-slate-700', 'dark:hover:text-white');
        }, 300);
      }
    },

    navigateNext() {
      const calendarApi = this.$refs.calendar.getApi();
      calendarApi.next();
    },


    // Handle sidemenu toggle (left sidebar) - resize calendar
    handleSidemenuToggle() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.calendar) {
            const calendarApi = this.$refs.calendar.getApi();
            calendarApi.updateSize();
          }
        }, 300);
      });
    },

    // Toggle sidebar (right sidebar)
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;

      // Wait for the sidebar animation to complete, then refresh calendar
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.calendar) {
            const calendarApi = this.$refs.calendar.getApi();
            // Force a re-render to ensure proper sizing
            calendarApi.updateSize();
          }
        }, 300); // 300ms matches the transition duration in the CSS
      });
    },

    // Check connection status and fetch email
    async checkConnectionStatus() {
      try {
        const headers = this.getAuthHeaders();
        const response = await axios.get('/api/v1/integrations_responza', {
          headers,
          withCredentials: true,
          params: { type: 'google_calendar' },
        });

        if (response.data) {
          this.calendarConnected = response.data.active === true;
          this.connectedEmail = response.data.email || null;
        }
      } catch (err) {
        // Silent fail - don't show error to user
        console.error('Failed to check connection status:', err);
      }
    },

    // Handle connect button click
    async handleConnect() {
      this.closeConnectionDropdown();
      this.loading = true;
      this.loadingMessage = 'Conectando...';
      this.error = null;
      try {
        const headers = this.getAuthHeaders();
        const response = await axios.get('/api/v1/integrations_responza', {
          headers,
          withCredentials: true,
          params: { type: 'google_calendar' },
        });

        // Check for server-side errors
        if (response.data && response.data.error) {
          throw new Error(response.data.error);
        }

        // Check if we have an authorization URL
        if (response.data && response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error('URL de autorização não encontrada na resposta');
        }
      } catch (err) {
        //console.error('Falha ao conectar à Agenda do Google:', err);
        this.error = 'Falha ao conectar. Tente novamente.';
      } finally {
        this.loading = false;
      }
    },

    // Update calendar integration permissions
    async updateCalendarIntegration() {
      this.loading = true;
      this.loadingMessage = 'Atualizando permissões...';
      this.error = null;
      try {
        const headers = this.getAuthHeaders();
        const response = await axios.get('/api/v1/integrations_responza', {
          headers,
          withCredentials: true,
          params: { type: 'google_calendar', update: true },
        });

        // Check for server-side errors
        if (response.data && response.data.error) {
          throw new Error(response.data.error);
        }

        // Check if we have an authorization URL
        if (response.data && response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error('URL de autorização de atualização não encontrada na resposta');
        }
      } catch (err) {
        //console.error('Falha ao atualizar a integração com a Agenda do Google:', err);
        this.error = 'Falha ao atualizar a integração. Tente novamente.';
      } finally {
        this.loading = false;
      }
    },

    // Connection dropdown methods
    openConnectionDropdown() {
      this.showConnectionDropdown = true;
    },

    closeConnectionDropdown() {
      this.showConnectionDropdown = false;
    },

    handleUpdateConnection() {
      this.closeConnectionDropdown();
      this.updateCalendarIntegration();
    },

    async handleDisconnect() {
      this.closeConnectionDropdown();
      this.loading = true;
      this.loadingMessage = 'Desconectando...';
      this.error = null;
      try {
        const headers = this.getAuthHeaders();
        const response = await axios.delete('/api/v1/integrations_responza', {
          headers,
          withCredentials: true,
        });

        if (response.status === 200) {
          this.calendarConnected = false;
          // Refresh the calendar to clear events
          if (this.$refs.calendar) {
            const calendarApi = this.$refs.calendar.getApi();
            calendarApi.refetchEvents();
          }
        } else {
          throw new Error('Falha ao desconectar a Agenda do Google');
        }
      } catch (err) {
        //console.error('Erro ao desconectar a Agenda do Google:', err);
        this.error = 'Falha ao desconectar. Tente novamente.';
      } finally {
        this.loading = false;
      }
    },

    // Handle date input
    handleDateInput() {
      if (!this.dateInputValue.trim()) return;
      
      // Try to parse the date input
      const inputDate = new Date(this.dateInputValue);
      
      // Check if the date is valid
      if (isNaN(inputDate.getTime())) {
        // Show error for invalid date
        //console.error('Data inválida:', this.dateInputValue);
        return;
      }
      
      // Navigate calendar to the entered date
      if (this.$refs.calendar) {
        const calendarApi = this.$refs.calendar.getApi();
        calendarApi.gotoDate(inputDate);
        this.dateInputValue = ''; // Clear input after navigation
      }
    },

    // Handle professional filter change
    onProfessionalChange() {
      if (this.$refs.calendar) {
        this.$refs.calendar.getApi().refetchEvents();
      }
    },

    // Handle Responza filter change
    onResponzaFilterChange() {
      if (this.$refs.calendar) {
        this.$refs.calendar.getApi().refetchEvents();
      }
    },

    // Toggle Responza filter (for collapsed sidebar)
    toggleResponzaFilter() {
      this.showOnlyResponzaEvents = !this.showOnlyResponzaEvents;
      this.onResponzaFilterChange();
    },

    // Professional dropdown methods
    openProfessionalDropdown() {
      this.showProfessionalDropdown = true;
    },

    closeProfessionalDropdown() {
      this.showProfessionalDropdown = false;
    },

    selectProfessional(professionalId) {
      this.selectedProfessionalId = professionalId;
      this.closeProfessionalDropdown();
      this.onProfessionalChange();
    },

    // View dropdown methods
    openViewDropdown() {
      this.showViewDropdown = true;
    },

    closeViewDropdown() {
      this.showViewDropdown = false;
    },

    selectView(viewName, viewLabel) {
      this.selectedViewLabel = viewLabel;
      this.closeViewDropdown();
      this.changeView(viewName);
    },

    // Load professionals from API
    async loadProfessionals() {
      try {
        const headers = this.getAuthHeaders();
        const response = await axios.get('/api/v1/professionals', {
          headers,
          withCredentials: true,
          params: {
            per_page: 100 // Get more professionals to avoid pagination issues
          }
        });
        // The API returns paginated data with professionals in the 'data' property
        this.professionals = response.data.data || [];
      } catch (error) {
        //console.error('Erro ao carregar profissionais:', error);
        // Don't show error to user for this background operation
      }
    },

    // Check if user has debug permissions
    async checkDebugMode() {
      try {
        const headers = this.getAuthHeaders();
        const response = await axios.get('/api/v1/check_debug_mode', {
          headers,
          withCredentials: true
        });
        this.isDebugMode = response.data.debug_mode === true;
      } catch (error) {
        //console.error('Erro ao verificar modo debug:', error);
        this.isDebugMode = false;
      }
    },

    // Copy Google Calendar token to clipboard
    async copyGoogleToken() {
      try {
        const headers = this.getAuthHeaders();
        const response = await axios.get('/api/v1/debug/google_calendar_token', {
          headers,
          withCredentials: true
        });

        if (response.data.success && response.data.token) {
          await navigator.clipboard.writeText(response.data.token);
          alert('Token do Google Calendar copiado para a área de transferência!');
        } else {
          alert('Token não disponível: ' + (response.data.error || 'Erro desconhecido'));
        }
      } catch (error) {
        //console.error('Erro ao copiar token:', error);
        if (error.response && error.response.status === 403) {
          alert('Acesso negado. Você não tem permissão para acessar esta funcionalidade de debug.');
        } else if (error.response && error.response.status === 404) {
          alert('Google Calendar não está conectado.');
        } else {
          alert('Erro ao buscar token: ' + (error.response?.data?.error || error.message));
        }
      }
    },

    // Get professional color from meta_data using colorId
    getProfessionalColor(professionalId) {
      const professional = this.professionals.find(p => p.id && p.id.toString() === professionalId?.toString());
      
      // Define predefined colors matching GeneralProfessionalModal
      const predefinedColors = [
        { colorId: 11, name: 'Tomate', value: '#D50000' },
        { colorId: 4, name: 'Flamingo', value: '#E67C73' },
        { colorId: 6, name: 'Tangerina', value: '#F4511E' },
        { colorId: 5, name: 'Banana', value: '#F6BF26' },
        { colorId: 2, name: 'Sálvia', value: '#33B679' },
        { colorId: 10, name: 'Manjericão', value: '#0B8043' },
        { colorId: 1, name: 'Pavão', value: '#039BE5' },
        { colorId: 9, name: 'Mirtilo', value: '#3F51B5' },
        { colorId: 7, name: 'Lavanda', value: '#7986CB' },
        { colorId: 3, name: 'Uva', value: '#8E24AA' },
        { colorId: 8, name: 'Grafite', value: '#616161' }
      ];
      
      if (professional && professional.meta_data) {
        // Check for new colorId system
        if (professional.meta_data.colorId) {
          const color = predefinedColors.find(c => c.colorId === professional.meta_data.colorId);
          if (color) {
            return color.value;
          }
        }
        // Fallback to old professional_color if exists
        if (professional.meta_data.professional_color) {
          return professional.meta_data.professional_color;
        }
      }
      
      return '#616161'; // Default to Grafite (gray) color for events without professionals
    },

    // Get professional name by ID
    getProfessionalNameById(professionalId) {
      if (!professionalId) return 'Profissional não encontrado';
      const professional = this.professionals.find(p => p.id && p.id.toString() === professionalId.toString());
      return professional ? professional.name : 'Profissional não encontrado';
    },

    // Truncate description with ellipsis
    truncateDescription(description) {
      if (!description) return '';
      if (description.length <= 80) return description;
      return description.substring(0, 80) + '...';
    },

    // Format date for tooltip
    formatTooltipDate(start, end) {
      if (!start) return '';
      
      const startDate = new Date(start);
      const endDate = end ? new Date(end) : null;
      
      const options = {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      
      let formatted = startDate.toLocaleString('pt-BR', options);
      
      if (endDate && endDate.getTime() !== startDate.getTime()) {
        // If end date is different from start date, show range
        const endFormatted = endDate.toLocaleString('pt-BR', options);
        formatted += ` - ${endFormatted}`;
      }
      
      return formatted;
    },

    // Format date only for tooltip: Tuesday, July 22
    formatTooltipDateOnly(start) {
      if (!start) return '';

      const startDate = new Date(start);

      // Get day name
      const dayName = startDate.toLocaleDateString('pt-BR', { weekday: 'long' });

      // Get month and day
      const dateStr = startDate.toLocaleDateString('pt-BR', { month: 'long', day: 'numeric' });

      const formatted = `${dayName}, ${dateStr}`;

      // Capitalize first letter
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    },

    // Format time only for tooltip: 11:00 - 12:00
    formatTooltipTimeOnly(start, end) {
      if (!start) return '';

      const startDate = new Date(start);
      const endDate = end ? new Date(end) : null;

      const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
      const startTime = startDate.toLocaleTimeString('pt-BR', timeOptions);

      if (endDate) {
        const endTime = endDate.toLocaleTimeString('pt-BR', timeOptions);
        return `${startTime} - ${endTime}`;
      }

      return startTime;
    },

    // Handle event mouse enter for tooltip
    handleEventMouseEnter(mouseEnterInfo) {
      const event = mouseEnterInfo.event;
      const eventElement = mouseEnterInfo.el; // Get the event DOM element
      
      this.hoveredEvent = {
        title: event.title,
        start: event.start,
        end: event.end,
        extendedProps: event.extendedProps
      };
      
      // Position tooltip relative to event element
      this.positionTooltipRelativeToElement(eventElement);
    },

    // Handle event mouse leave
    handleEventMouseLeave() {
      this.hoveredEvent = null;
      this.tooltipStyle = {};
    },

    // Position tooltip relative to event element
    positionTooltipRelativeToElement(eventElement) {
      // Give Vue a tick so that $refs.tooltip is actually rendered
      this.$nextTick(() => {
        const tooltip = this.$refs.tooltip;
        if (!tooltip || !eventElement) return;

        const offset = 10; // space between event and tooltip
        const eventRect = eventElement.getBoundingClientRect();
        const { innerWidth, innerHeight } = window;
        const { width: ttW, height: ttH } = tooltip.getBoundingClientRect();

        // Align left side of tooltip with left side of event
        let left = eventRect.left;
        
        // Try to place it below the event
        let top = eventRect.bottom + offset;

        // If placing it below would overflow the viewport, place it above
        if (top + ttH > innerHeight - offset) {
          top = eventRect.top - ttH - offset;
        }

        // Make sure the tooltip doesn't go off the right edge
        if (left + ttW > innerWidth - offset) {
          left = innerWidth - ttW - offset;
        }

        // Make sure the tooltip doesn't go off the left edge
        if (left < offset) {
          left = offset;
        }

        this.tooltipStyle = {
          left: `${left}px`,
          top:  `${top}px`
        };
      });
    }
  },
  mounted() {
    // Attach event handlers to calendar
    this.calendarOptions.datesSet = (dateInfo) => {
      this.handleDatesSet(dateInfo);
      this.onDatesSet(dateInfo);
    };

    // Load professionals on component mount
    this.loadProfessionals();

    // Check connection status and fetch email
    this.checkConnectionStatus();

    // Check if user has debug permissions
    this.checkDebugMode();

    // Set up automatic refresh every 5 minutes (300000ms)
    this.refreshTimer = setInterval(() => {
      this.refreshCalendar();
    }, 300000);

    // Listen for sidemenu toggle to resize calendar
    emitter.on(BUS_EVENTS.TOGGLE_SIDEMENU, this.handleSidemenuToggle);
  },
  beforeUnmount() {
    // Clear the refresh interval when component is unmounted
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }

    // Remove sidemenu toggle listener
    emitter.off(BUS_EVENTS.TOGGLE_SIDEMENU, this.handleSidemenuToggle);
  }
}
</script>

<style>
/* FullCalendar customizations */
.fc .fc-toolbar-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1rem;
}

.fc .fc-button-primary {
  background-color: #3788d8;
  border-color: #3788d8;
}

.fc .fc-button-primary:hover {
  background-color: #3788d8;
  border-color: #3788d8;
}

.fc .fc-col-header-cell-cushion {
  text-decoration: none !important;
  color: #0c0d11;
  text-transform: uppercase;
}

.fc .fc-daygrid-day-number {
  text-decoration: none !important;
  color: #0c0d11;
}

.fc .fc-col-header-cell {
  border-left: none;
  border-right: none;
}

.fc .fc-daygrid-day.fc-day-today {
  background-color: rgba(67, 97, 238, 0.1);
}

/* Dark mode styles */
.dark .fc {
  background-color: rgb(var(--slate-2));
}

.dark .fc .fc-toolbar-title {
  color: rgb(var(--slate-12));
}

.dark .fc .fc-col-header-cell-cushion,
.dark .fc .fc-daygrid-day-number {
  color: rgb(var(--slate-12)) !important;
}

.dark .fc .fc-col-header-cell {
  background-color: rgb(var(--slate-3));
}

.dark .fc .fc-daygrid-day {
  background-color: rgb(var(--slate-2));
  border-color: rgb(var(--slate-4));
}

.dark .fc .fc-daygrid-day.fc-day-today {
  background-color: rgba(67, 97, 238, 0.2);
}

.dark .fc .fc-scrollgrid {
  border-color: rgb(var(--slate-4));
}

.dark .fc .fc-scrollgrid-sync-table {
  border-color: rgb(var(--slate-4));
}

.dark .fc td,
.dark .fc th {
  border-color: rgb(var(--slate-4));
}

.dark .fc .fc-daygrid-day-frame {
  background-color: rgb(var(--slate-2));
}

.fc-event {
  cursor: pointer;
  border-radius: 8px;
  padding: 2px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.fc-event:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
  background-color: #ffffff !important;
  opacity: 1 !important;
}

/* Ensure the event content stays opaque on hover */
.fc-event:hover .fc-event-main {
  opacity: 1 !important;
  background-color: #ffffff !important;
}

.fc-event:hover .fc-event-title,
.fc-event:hover .fc-event-time {
  opacity: 1 !important;
  color: #0c0d11 !important; /* Dark text color for contrast on white background */
}

/* List view styling */
.fc-list-day-cushion {
  background-color: #f8f8f8 !important;
}

.fc-list-event-title a {
  color: #0c0d11 !important;
  text-decoration: none !important;
}

.fc-list-event:hover td {
  background-color: #f0f0f3 !important;
}


/* Animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}


.detalhes-button-group {
  &:hover {
    .detalhes-button-main,
    .detalhes-button-icon {
      @apply bg-n-alpha-2;
    }
  }

  .detalhes-button-main,
  .detalhes-button-icon {
    transition: background-color 0.15s ease-in-out;
    
    &:hover {
      @apply bg-n-alpha-2;
    }
  }
}

.button-group {
  &:hover {
    button {
      @apply bg-n-slate-3 dark:bg-n-slate-11;
    }
  }
  
  button {
    @apply transition-colors duration-200;

    &:hover {
      @apply bg-n-slate-3 dark:bg-n-slate-11;
    }
  }
}

/* Overlay styles with backdrop filter and gradient backgrounds */
.error-overlay,
.alert-overlay {
  backdrop-filter: blur(1px);
  background-image: linear-gradient(to top, #fff 30%, rgba(255, 255, 255, 0.4));
}

.dark .error-overlay,
.dark .alert-overlay {
  background-image: linear-gradient(to top, rgba(22, 22, 22, 0.95) 30%, rgba(12, 12, 12, 0.85));
}

/* Custom scrollbar styles - lighter and subtle (matching ChatList) */
.calendar-wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.calendar-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.calendar-wrapper::-webkit-scrollbar-thumb {
  background-color: rgb(226 232 240 / 0.5); /* Light gray with transparency */
  border-radius: 4px;
}

/* Dark mode scrollbar */
.dark .calendar-wrapper::-webkit-scrollbar-thumb {
  background-color: rgb(71 85 105 / 0.5); /* Subtle gray for dark mode */
}

/* Firefox support */
.calendar-wrapper {
  scrollbar-width: normal;
  scrollbar-color: rgb(231 231 231 / 0.5) transparent;
}

.dark .calendar-wrapper {
  scrollbar-color: rgb(71 85 105 / 0.5) transparent;
}
</style>